import { CalendarRange, Check, X } from 'lucide-react';
import { useState } from 'react';

import { DatePicker } from '@/components/ui';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import cn from '@/utils/cn';

export interface DateRange {
  from?: string;
  to?: string;
  label: string;
}

type Preset = '1m' | '3m' | 'ytd' | 'custom';

const PRESETS: { key: Preset; label: string }[] = [
  { key: '1m', label: '1 Month' },
  { key: '3m', label: '3 Months' },
  { key: 'ytd', label: 'YTD' },
  { key: 'custom', label: 'Custom Range' },
];

const pad = (n: number): string => String(n).padStart(2, '0');
const toInput = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmt = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const presetToRange = (preset: Preset): { from: string; to: string } => {
  const to = new Date();
  const from = new Date();
  if (preset === '1m') from.setMonth(from.getMonth() - 1);
  if (preset === '3m') from.setMonth(from.getMonth() - 3);
  if (preset === 'ytd') {
    from.setMonth(0);
    from.setDate(1);
  }
  return { from: toInput(from), to: toInput(to) };
};

interface DateRangeFilterDialogProps {
  value: DateRange | null;
  onApply: (range: DateRange | null) => void;
}

export function DateRangeFilterDialog({ value, onApply }: DateRangeFilterDialogProps) {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState<Preset>('1m');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const pick = (p: Preset) => {
    setPreset(p);
    if (p !== 'custom') {
      const r = presetToRange(p);
      setFrom(r.from);
      setTo(r.to);
    }
  };

  const apply = () => {
    if (!from || !to || from > to) return;
    const label =
      PRESETS.find((p) => p.key === preset)?.label === 'Custom Range'
        ? `${fmt(from)} - ${fmt(to)}`
        : `${PRESETS.find((p) => p.key === preset)?.label} (${fmt(from)} - ${fmt(to)})`;
    onApply({ from, to, label });
    setOpen(false);
  };

  return (
    <>
      <div className="flex min-w-0 items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          className="min-w-0 max-w-[14rem] gap-1.5"
          onClick={() => {
            if (!from && !to) pick('1m');
            setOpen(true);
          }}
        >
          <CalendarRange className="h-4 w-4 shrink-0" />
          <span className="truncate">{value ? value.label : 'Date Range'}</span>
        </Button>
        {value && (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Clear date filter"
            className="size-9 shrink-0"
            onClick={() => onApply(null)}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Filter by date</DialogTitle>
            <DialogDescription>Choose a preset range or pick a custom one.</DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => pick(p.key)}
                className={cn(
                  'rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors',
                  preset === p.key
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-background text-muted-foreground hover:text-foreground',
                )}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-xs">From</Label>
              <DatePicker
                value={from}
                onChange={(v) => {
                  setPreset('custom');
                  setFrom(v);
                }}
              />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">To</Label>
              <DatePicker
                value={to}
                onChange={(v) => {
                  setPreset('custom');
                  setTo(v);
                }}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              className="gap-1.5"
              onClick={apply}
              disabled={!from || !to || from > to}
            >
              <Check className="h-4 w-4" />
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
